import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const ItemRow = ({ item, catalog, canEdit, onChanged }) => {
  const [name, setName] = useState(item.name);
  const save = async () => {
    if (name === item.name) return;
    try { await api.patch(`/admin/catalogs/${catalog}/items/${item.id}`, { name, order: item.order }); toast.success("Ítem actualizado"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); setName(item.name); }
  };
  const toggle = async () => {
    try { await api.post(`/admin/catalogs/${catalog}/items/${item.id}/toggle`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-[#f0f2ee] last:border-0 ${item.active ? "" : "opacity-50"}`} data-testid={`catalog-item-${item.code}`}>
      <span className="font-mono text-[11px] text-[#8a9584] w-28 truncate">{item.code}</span>
      <Input data-testid={`catalog-item-name-${item.code}`} disabled={!canEdit} value={name} onChange={(e) => setName(e.target.value)} onBlur={save} className="h-9 flex-1" />
      <Switch data-testid={`catalog-item-toggle-${item.code}`} disabled={!canEdit} checked={item.active} onCheckedChange={toggle} />
    </div>
  );
};

const AddItem = ({ catalog, onAdded }) => {
  const [f, setF] = useState({ code: "", name: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/admin/catalogs/${catalog}/items`, { ...f, order: 999 }); setF({ code: "", name: "" }); toast.success("Ítem agregado"); onAdded(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 p-4 border-b border-[#e4e8e1] bg-[#fafbf9]" data-testid="add-catalog-item-form">
      <Input data-testid="new-item-code-input" placeholder="Código" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/\s/g, "_") })} className="sm:w-40" required />
      <Input data-testid="new-item-name-input" placeholder="Nombre" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
      <Button data-testid="add-catalog-item-btn" className="rounded-full"><Plus size={15} className="mr-1" />Agregar</Button>
    </form>
  );
};

const NewCatalog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({ code: "", name: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/catalogs", f); toast.success("Maestro creado"); onCreated(f.code); setF({ code: "", name: "" }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-catalog-dialog">
        <DialogHeader><DialogTitle>Nuevo maestro</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Código" hint="minúsculas y guion bajo, ej: tipos_turno"><Input data-testid="new-catalog-code-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toLowerCase().replace(/\s/g, "_") })} required /></Field>
          <Field label="Nombre"><Input data-testid="new-catalog-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
          <DialogFooter><Button data-testid="new-catalog-submit-btn" className="rounded-full">Crear</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Catalogs() {
  const { can } = useAuth();
  const canEdit = can("admin.maestros.editar");
  const [catalogs, setCatalogs] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const loadCatalogs = () => api.get("/admin/catalogs").then((r) => { setCatalogs(r.data); setActive((a) => a || r.data[0]?.code); });
  const loadItems = () => active && api.get(`/admin/catalogs/${active}/items`).then((r) => setItems(r.data));
  useEffect(() => { loadCatalogs(); }, []);
  useEffect(() => { loadItems(); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = catalogs.find((c) => c.code === active);

  return (
    <div data-testid="catalogs-page">
      <PageHeader eyebrow="Administración" title="Maestros del sistema" subtitle="Catálogos configurables. Los ítems no se eliminan: se desactivan para conservar el historial."
        actions={canEdit && <Button data-testid="create-catalog-btn" variant="outline" className="rounded-full" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" />Nuevo maestro</Button>} />
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="syc-card p-2 h-fit flex lg:flex-col gap-1 overflow-x-auto" data-testid="catalogs-list">
          {catalogs.map((c) => (
            <button key={c.code} data-testid={`catalog-${c.code}`} onClick={() => setActive(c.code)}
              className={`text-left rounded-lg px-3 py-2 shrink-0 transition-colors ${c.code === active ? "bg-[#258F00] text-white" : "hover:bg-[#F7F7F7]"}`}>
              <div className="text-sm font-semibold whitespace-nowrap">{c.name}</div>
              <div className={`text-[11px] ${c.code === active ? "text-white/80" : "text-[#8a9584]"}`}>{c.count} ítems</div>
            </button>
          ))}
        </div>
        <div className="syc-card overflow-hidden h-fit" data-testid="catalog-items">
          <div className="px-5 py-4 border-b border-[#e4e8e1] font-bold">{current?.name}</div>
          {canEdit && active && <AddItem catalog={active} onAdded={() => { loadItems(); loadCatalogs(); }} />}
          {items.length === 0 ? <EmptyState title="Sin ítems" text="Agregue los valores que utilizará S&C." /> :
            items.map((i) => <ItemRow key={i.id} item={i} catalog={active} canEdit={canEdit} onChanged={loadItems} />)}
        </div>
      </div>
      <NewCatalog open={open} onOpenChange={setOpen} onCreated={(code) => { setOpen(false); setActive(code); loadCatalogs(); }} />
    </div>
  );
}
